import { InjectionToken } from '@angular/core';

/**
 * Contract the storefront library uses to decide whether the current user may
 * manage the store (create/edit products, orders, config) versus view-only.
 *
 * The library is dual-consumed (admin portal + member portal), each with its
 * own access model, so it declares this minimal contract and each app provides
 * an adapter via {@link STOREFRONT_ACCESS} (mirrors DASHBOARD_ACCESS).
 */
export interface StorefrontAccessProvider {
  /** True if the current user may manage the store; false = read-only. */
  canManage(): boolean;

  /**
   * True if the current user holds at least the given access tier on the
   * storefront module. Stricter than {@link canManage}, which collapses
   * edit-or-manage into one "can mutate" boolean — some destructive admin
   * actions (e.g. deleting a coupon, cancelling an order, sending a receipt)
   * are gated to the "manage" tier specifically. Optional: hosts that don't
   * wire this fall back to {@link canManage} for both tiers.
   */
  hasLevel?(level: 'edit' | 'manage'): boolean;
}

/**
 * Injection token for the active {@link StorefrontAccessProvider}. Defaults to
 * view-only so a consumer that forgets to wire an adapter gets the safe
 * behavior rather than accidental manage access.
 */
export const STOREFRONT_ACCESS = new InjectionToken<StorefrontAccessProvider>(
  'STOREFRONT_ACCESS',
  {
    providedIn: 'root',
    factory: () => ({ canManage: () => false, hasLevel: () => false }),
  },
);
